import {HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from "@angular/common/http";
import {Injectable, Injector, OnDestroy} from "@angular/core";
import {Observable} from "rxjs/Observable";
import {Router} from "@angular/router";
import {Subject} from "rxjs";
import {of} from "rxjs/observable/of";
import {mapTo} from "rxjs/operators/mapTo";
import {catchError, first, map, shareReplay, switchMap, tap} from "rxjs/operators";
import {ApplicationPropertiesService} from "@synisys/idm-application-properties-service-client-js";
import {fromPromise} from "rxjs/observable/fromPromise";

@Injectable()
export class MeTreeShakingInterceptor implements HttpInterceptor, OnDestroy {

	private destroy$: Subject<boolean> = new Subject<boolean>();
	private deServiceUrl$: Observable<string>;
	private classifierServiceUrl$: Observable<string>;

	private httpClient: HttpClient;
	applicationProperties: ApplicationPropertiesService;

	private isInGrant: boolean;

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.unsubscribe();
	}

	constructor(
		private router: Router,
		private injector: Injector
	) {

	}

	private injectDependencies(): void {
		this.httpClient = this.injector.get(HttpClient);
		this.applicationProperties = this.injector.get(ApplicationPropertiesService);
		this.deServiceUrl$ = fromPromise(this.applicationProperties.getProperty('de-service-url')).pipe(shareReplay());
		this.classifierServiceUrl$ = fromPromise(this.applicationProperties.getProperty('classifier-service-url')).pipe(shareReplay());
	}

	private isNeededRequest(req: HttpRequest<any>): boolean {
		return req.method === 'GET' && req.url.indexOf('/frameworks/tree/') !== -1;
	}

	private isInNeededForm(): Observable<boolean> {
		this.isInGrant = this.isInGrantForm();
		return of(this.isInGrant || this.isInProposalForm());
	}

	private isInGrantForm(): boolean {
		let currentUrl = this.router.url;
		if (currentUrl.indexOf('?') !== -1) {
			currentUrl = currentUrl.substring(0, currentUrl.indexOf('?'));
		}
		return currentUrl.indexOf('/dynamic-de/Grant/') !== -1 && (
			currentUrl.indexOf('1000020') !== -1 || currentUrl.indexOf('1000021') !== -1
		);
	}

	private isInProposalForm(): boolean {
		let currentUrl = this.router.url;
		if (currentUrl.indexOf('?') !== -1) {
			currentUrl = currentUrl.substring(0, currentUrl.indexOf('?'));
		}
		return currentUrl.indexOf('/dynamic-de/AgraResultFramework/') !== -1 && (
			currentUrl.indexOf('1000000') !== -1 || currentUrl.indexOf('1000001') !== -1
		);
	}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		!this.httpClient && this.injectDependencies();
		if (!this.isNeededRequest(req)) return next.handle(req);
		return this.isInNeededForm()
			.pipe(
				switchMap(
					needToIntercept => next.handle(req)
						.pipe(
							switchMap((response: HttpResponse<Tree>) =>
								needToIntercept && response.body ? this.processResponse(response) : of(response))
						)
				),
				catchError(error => {
					console.error(error);
					return next.handle(req);
				})
			);
	}

	private getInterventions(): Observable<FlatClassifier[]> {
		return this.getProposalId()
			.pipe(first())
			.pipe(
				tap(id => !id && Observable.throw('Proposal Id not found')),
				switchMap(proposalInstanceId => this.loadProposalInterventions(proposalInstanceId)),
				catchError((error) => {
					console.error(`Couldn't load interventions: ${error}`)
					return of([]);
				})
			);
	}

	private getDonors(): Observable<SimpleClassifier[]> {
		if (this.isInGrant) {
			return this.getGrantId()
				.pipe(first())
				.pipe(
					tap(id => !id && Observable.throw('Grant Id not found')),
					switchMap(grantInstanceId => this.loadGrantDonors(grantInstanceId)),
					catchError((error) => {
						console.error(`Couldn't load donors: ${error}`)
						return of([]);
					})
				);
		} else {
			return this.getProposalId()
				.pipe(first())
				.pipe(
					tap(id => !id && Observable.throw('Proposal Id not found')),
					switchMap(proposalInstanceId => this.loadProposalDonors(proposalInstanceId)),
					catchError((error) => {
						console.error(`Couldn't load donors: ${error}`)
						return of([]);
					})
				);
		}
	}

	private getProposalId(): Observable<number> {
		return this.isInGrantForm() ? this.loadGrantProposal() : this.loadRFProposal();
	}

	private getGrantId(): Observable<number> {
		let currUrl = this.router.url;
		if (currUrl.indexOf('?') !== -1) {
			currUrl = currUrl.substring(0, currUrl.indexOf('?'));
		}
		const grantInstanceId = +currUrl.substring(currUrl.lastIndexOf('/') + 1);
		return of(grantInstanceId);
	}

	private loadRFProposal(): Observable<number> {
		let currUrl = this.router.url;
		let urlBeforeQ = currUrl;
		if (currUrl.indexOf('?') !== -1) {
			urlBeforeQ = currUrl.substring(0, currUrl.indexOf('?'));
		}
		let indexOfLastSlash = urlBeforeQ.indexOf('1000000') !== -1 ? urlBeforeQ.indexOf('1000000') + '1000000'.length
			: urlBeforeQ.indexOf('1000001') + '1000001'.length
		let agraRfInstanceId: number = +urlBeforeQ.substring(indexOfLastSlash + 1);
		return this.deServiceUrl$.pipe(
			first(),
			switchMap(
				url =>
					this.httpClient.get<{ data: { proposalInstanceId: number } }>(
						`${url}/entity/AgraResultFramework/instance/${agraRfInstanceId}?languageId=7&metaFields=proposalInstanceId`
					)
			),
			map(agraRf => agraRf.data.proposalInstanceId),
			first()
		);
	}

	private loadGrantProposal(): Observable<number> {
		let currUrl = this.router.url;
		if (currUrl.indexOf('?') !== -1) {
			currUrl = currUrl.substring(0, currUrl.indexOf('?'));
		}
		const grantInstanceId = +currUrl.substring(currUrl.lastIndexOf('/') + 1);
		if (isNaN(grantInstanceId)) return of(null);
		return this.deServiceUrl$.pipe(
			switchMap(
				url =>
					this.httpClient.get<{ data: { proposalInstanceId: number } }>(
						`${url}/entity/Grant/instance/${grantInstanceId}?languageId=7&metaFields=proposalInstanceId`
					)
			),
			map(response => response.data.proposalInstanceId)
		)
	}

	private loadProposalInterventions(proposalInstanceId: number): Observable<FlatClassifier[]> {
		return this.deServiceUrl$.pipe(
			switchMap(
				url =>
					this.httpClient.get<{ data: { interventionAreas: number[] } }>(
						`${url}/entity/Proposal/instance/${proposalInstanceId}?languageId=7&metaFields=interventionAreas`
					)
			),
			map(response => response.data.interventionAreas),
			switchMap(
				areaIds => this.classifierServiceUrl$.pipe(
					switchMap(
						url => this.httpClient.get<{ data: FlatClassifier[] }>(
							`${url}/classifiers/InterventionArea?languageId=7`
						)
					),
					map(classifiers => classifiers.data.filter(cl => areaIds.some(id => id === cl.entityId)))
				)
			),
			switchMap(this.addFrameworkIdToIntervention.bind(this))
		);
	}

	private loadProposalDonors(proposalInstanceId: number): Observable<SimpleClassifier[]> {
		return this.deServiceUrl$.pipe(
			switchMap(
				url =>
					this.httpClient.get<{ data: { ProposalProposedDonorandFundCode: any[] } }>(
						`${url}/entity/Proposal/instance/${proposalInstanceId}?languageId=7&metaFields=ProposalProposedDonorandFundCode`
					)
			),
			map(response => response.data.ProposalProposedDonorandFundCode.map(f => f.donorId)),
			switchMap(
				donorIds => this.classifierServiceUrl$.pipe(
					switchMap(
						url => this.httpClient.get<{ data: SimpleClassifier[] }>(
							`${url}/classifiers/Donor?languageId=7`
						)
					),
					map(classifiers => classifiers.data
						.filter(cl => donorIds.some(id => id === cl.entityId)))
				)
			)
		);
	}

	private loadGrantDonors(grantInstanceId: number): Observable<SimpleClassifier[]> {
		return this.deServiceUrl$.pipe(
			switchMap(
				url =>
					this.httpClient.get<{ data: { GrantCommitment: any[] } }>(
						`${url}/entity/Grant/instance/${grantInstanceId}?languageId=7&metaFields=GrantCommitment`
					)
			),
			map(response => response.data.GrantCommitment.map(f => f.donorId)),
			switchMap(
				donorIds => this.classifierServiceUrl$.pipe(
					switchMap(
						url => this.httpClient.get<{ data: SimpleClassifier[] }>(
							`${url}/classifiers/Donor?languageId=7`
						)
					),
					map(classifiers => classifiers.data
						.filter(cl => donorIds.some(id => id === cl.entityId)))
				)
			)
		);
	}

	private addFrameworkIdToIntervention(interventions: FlatClassifier[]): Observable<FlatClassifier[]> {
		if (!interventions.length) return of(interventions);
		return this.classifierServiceUrl$.pipe(
			switchMap(
				url => this.httpClient.get<{ frameworkId: number }>(
					`${url}/classifiers/FrameworkStrategy/${interventions[0].frameworkStrategyId}?languageId=7`
				)
			),
			tap(strategy => interventions.forEach(intervention => intervention.frameworkId = strategy.frameworkId)),
			mapTo(interventions)
		);
	}

	private processResponse(response: HttpResponse<Tree>): Observable<HttpEvent<any>> {
		return this.processAndShakeTree(response.body)
			.pipe(map(tree => treeToResponse(tree, response)));
	}

	private processAndShakeTree(frameworkTree: Tree): Observable<Tree> {
		let frameworkId = frameworkTree.frameworkId;
		if (frameworkId === 1 || frameworkId == 48 || frameworkId === 49) {
			return this.getInterventions()
				.map(interventions =>
					this.interventionsFitTheFramework(interventions, frameworkTree)
						? this.changeItemsBasedOnInterventions(frameworkTree, interventions)
						: frameworkTree
				);
		} else if (frameworkId === 53) {
			return this.getDonors()
				.map(donors =>
					this.changeItemsBasedOnDonors(frameworkTree, donors)
				);
		} else {
			return of(frameworkTree);
		}
	}

	private interventionsFitTheFramework(interventions: FlatClassifier[], frameworkTree: Tree): boolean {
		return interventions.length && interventions[0].frameworkId === frameworkTree.frameworkId;
	}

	private changeItemsBasedOnInterventions(tree: Tree, interventions: FlatClassifier[]): Tree {
		const neededLevels: { [level: number]: { [rfItemId: number]: { allChildren: boolean } } } = interventions
			.reduce(
				(
					neededItems: { [level: number]: { [rfItemId: number]: { allChildren: boolean } } },
					item: FlatClassifier
				) => {
					if (!neededItems[item.level]) neededItems[item.level] = {};
					if (!neededItems[item.parentLevel]) neededItems[item.parentLevel] = {};
					neededItems[item.level][item.rfItemId] = {allChildren: true};
					neededItems[item.parentLevel][item.parentRfItemId] = {allChildren: false};
					return neededItems;
				},
				{}
			);
		if (Object.keys(neededLevels).length > 0 && Object.keys(neededLevels).indexOf('1') === -1) {
			neededLevels[1] = {};
			neededLevels[1][tree.items[0].id] = {allChildren: false};
		}
		return this.traverseTree(tree, neededLevels);
	}

	private changeItemsBasedOnDonors(tree: Tree, donors: SimpleClassifier[]): Tree {
		tree.items = tree.items.filter(item => donors.some(donor => donor.rfDonorId === item.id));
		return tree;
	}

	private traverseTree(tree: Tree, neededLevels: { [level: number]: { [rfItemId: number]: { allChildren: boolean } } }): Tree {
		tree.items = this.traverseTreeItems(tree.items, neededLevels);
		return tree;
	}

	private traverseTreeItems(treeItems: TreeItem[], neededLevels: { [level: number]: { [rfItemId: number]: { allChildren: boolean } } }): TreeItem[] {
		const filteredTreeItems = treeItems.filter(item => neededLevels[item.level] && neededLevels[item.level][item.id]);
		filteredTreeItems.forEach(
			item => item.items = neededLevels[item.level][item.id].allChildren
				? item.items
				: item.items && this.traverseTreeItems(item.items, neededLevels)
		);
		return filteredTreeItems;
	}
}

export type Tree = {
	frameworkId: number,
	items: TreeItem[]
}

export type TreeItem = {
	id: number,
	parentId: number,
	level: number,
	items: TreeItem[]
}

function treeToResponse(tree: Tree, existingResponse: HttpResponse<Tree>): HttpResponse<Tree> {
	return existingResponse.clone(
		{
			body: tree
		}
	);
}

interface FlatClassifier {
	entityId: number,
	level: number,
	rfItemId: number,
	parentLevel: number,
	parentRfItemId: number,
	frameworkStrategyId: number,
	frameworkId?: number
}

interface SimpleClassifier {
	entityId: number,
	rfItemId: number,
	rfDonorId: number
}
